/** "Setting up your account…" (backend SETUP-GATE plan D2): after self-signup the admin can sign in before the setup
 *  tasks gave them any permission, and the dashboard then opened empty. Above the Router, so nothing below it (the
 *  menu, the landing redirect, the my_permissions poll) runs until the account is ready.
 *
 *  - It asks `GET /v2/edit/onboarding/setup_status/` (the onboarding caller: a 403 is an answer, never a sign-out)
 *    only for a user without any permission, or one from the sign-up link (`setupFlag.ts`) whose permissions are not
 *    known. A user with permissions never sees it.
 *  - `pending`: the full-screen steps, read every SETUP_POLL_MS while the tab is shown; 5xx back off, a 429 waits its
 *    Retry-After (a tab shown again waits them out too); "taking longer than usual" after SETUP_SLOW_MS; no step moved
 *    for SETUP_STALL_MS reads as failed (still read, slowly: it may recover).
 *  - `ready`: my_permissions is read again and stored, then "/" is loaded afresh, so the landing opens the Onboarding
 *    page with the new permissions (a stale session would land on the empty home and stay there). Its cache may lag
 *    behind `ready`: read again after PERMISSIONS_RETRY_MS, then "/" is loaded anyway (that load is not pending, so it
 *    cannot loop). A `ready` the gate never showed `pending` for reloads the page the user asked for instead.
 *  - `failed` (only while pending): the support line, as after a long stall; read every SETUP_STALLED_POLL_MS from then
 *    on, since the setup run may still retry it. It claims no notification: nothing sends one.
 *  - Never blocks: the first read waits at most SETUP_FIRST_READ_WAIT_MS before the app shows (a late `pending` still
 *    brings the gate up), and an error of the endpoint shows the app. */
import { Alert, Box, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import * as React from 'react';

import { leaveDashboardFor } from '../auth/leaveDashboard';
import { usePageVisibility } from '../components/usePageVisibility';
import { ContextStore } from '../store/contextStore';
import { SUPPORT_EMAIL } from '../utils/support';
import { createCaller, createOnboardingApi, OnboardingApiError } from './api';
import { ProgressPanel } from './components';
import { SETUP_STEPS } from './labels';
import { clearSetupFlag, hasSetupFlag, permissionState, shouldCheckSetup } from './setupFlag';
import type { OnboardingLane, OnboardingSetupStatus } from './types';
import { backoffDelay } from './useOnboardingState';

export const SETUP_POLL_MS = 4000;
export const SETUP_MAX_BACKOFF_MS = 30_000;
/** Reads while the setup failed or looks stalled (the failed text is up, but it may still finish). */
export const SETUP_STALLED_POLL_MS = 30_000;
export const SETUP_FIRST_READ_WAIT_MS = 5000;
export const SETUP_SLOW_MS = 3 * 60_000;
export const SETUP_STALL_MS = 30 * 60_000;
/** After `ready`, the waits before my_permissions is read again while it still shows no access (~20 s in all). */
export const PERMISSIONS_RETRY_MS = [2000, 4000, 6000, 8000];

export const SETUP_TITLE = 'Setting up your account…';
export const SETUP_SLOW_TEXT = 'This is taking longer than usual.';
export const SETUP_FAILED_TEXT = "We couldn't finish setting up your account. Please contact";

const PERMISSION_ENDPOINTS = { myPermissions: { method: 'GET', path: '/v2/edit/user/my_permissions/' } } as const;

type Phase = 'app' | 'checking' | 'pending' | 'failed' | 'opening';
type Steps = OnboardingSetupStatus['steps'];

interface View {
    phase: Phase;
    steps: Steps;
    slow: boolean;
}

/** The steps as the onboarding page's lanes (their state words are the lanes'). */
export const stepLanes = (steps: Steps): OnboardingLane[] => steps.map((s) => ({
    key: s.key,
    label: SETUP_STEPS[s.key] || s.key,
    state: s.state,
    summary: '',
    progress: null,
}) as OnboardingLane);

function useSetupApi() {
    const { token, logout } = React.useContext(ContextStore) as any;
    const tokenRef = React.useRef(token);
    tokenRef.current = token;
    const logoutRef = React.useRef(logout);
    logoutRef.current = logout;
    return React.useMemo(() => {
        const options = {
            getToken: () => tokenRef.current,
            onUnauthorized: () => logoutRef.current?.(window.location.pathname),
        };
        const call = createCaller(PERMISSION_ENDPOINTS, options);
        return {
            setupStatus: createOnboardingApi(options).setupStatus,
            myPermissions: (signal?: AbortSignal) => call<{ cache?: any }>('myPermissions', { signal }),
        };
    }, []);
}

export default function SetupGate({ children }: { children: React.ReactNode }) {
    const { token, tourOperator, user, setUser, logout } = React.useContext(ContextStore) as any;
    const topId = tourOperator?.id ?? null;
    const api = useSetupApi();
    const visible = usePageVisibility();
    // Decided once per page load, from the session as it was stored.
    const [check] = React.useState(() => !!token && shouldCheckSetup(user?.permissions, hasSetupFlag(topId)));
    const [view, setView] = React.useState<View>({ phase: check ? 'checking' : 'app', steps: [], slow: false });

    const phase = React.useRef<Phase>(view.phase);
    const show = React.useCallback((next: Partial<View>) => {
        if (next.phase) {
            phase.current = next.phase;
        }
        setView((v) => ({ ...v, ...next }));
    }, []);
    const stopped = React.useRef(!check);
    const visibleRef = React.useRef(visible);
    visibleRef.current = visible;
    const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const inflight = React.useRef<AbortController | null>(null);
    const failures = React.useRef(0);
    /** No read before this time (a 429's Retry-After, a 5xx back-off), also when the tab is shown again. */
    const notBefore = React.useRef(0);
    const retryTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    /** When the gate first saw `pending`, and when a step last moved. */
    const progress = React.useRef<{ started: number; changed: number; steps: string } | null>(null);
    const setUserRef = React.useRef(setUser);
    setUserRef.current = setUser;

    // A user with permissions is never gated: the sign-up link's flag has done its job.
    React.useEffect(() => {
        if (!check) {
            clearSetupFlag(topId);
        }
    }, [check, topId]);

    const clearTimer = () => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    };

    const stop = React.useCallback((next?: Phase) => {
        stopped.current = true;
        if (next) {
            show({ phase: next });
        }
    }, [show]);

    /** `ready`: store the new permissions and load "/" afresh (the landing then opens the Onboarding page); when the
     *  gate never showed, reload the page the user asked for. */
    const finish = React.useCallback(() => {
        const sawPending = progress.current != null;
        stopped.current = true;
        if (sawPending) {
            show({ phase: 'opening' });
        }
        const target = sawPending ? '/' : `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const noAccessYet = (attempt: number) => {
            if (!sawPending) {
                clearSetupFlag(topId);
                show({ phase: 'app' });
            } else if (attempt < PERMISSIONS_RETRY_MS.length) {
                // The permissions cache may lag behind `ready`.
                retryTimer.current = setTimeout(() => readPermissions(attempt + 1), PERMISSIONS_RETRY_MS[attempt]);
            } else {
                clearSetupFlag(topId);
                leaveDashboardFor('/');
            }
        };
        const readPermissions = (attempt: number) => {
            api.myPermissions().then(
                (res) => {
                    const cache = res?.cache;
                    if (permissionState(cache) === 'some') {
                        clearSetupFlag(topId);
                        setUserRef.current?.((prev: any) => ({ ...prev, permissions: cache }));
                        leaveDashboardFor(target);
                    } else {
                        noAccessYet(attempt);
                    }
                },
                (e) => {
                    if (!(e instanceof OnboardingApiError && e.status === 401)) {
                        noAccessYet(attempt);
                    }
                },
            );
        };
        readPermissions(0);
    }, [api, show, topId]);

    React.useEffect(() => () => {
        if (retryTimer.current) {
            clearTimeout(retryTimer.current);
        }
    }, []);

    const read = React.useCallback(() => {
        clearTimer();
        inflight.current?.abort();
        if (stopped.current || !visibleRef.current) {
            return;
        }
        const ctrl = new AbortController();
        inflight.current = ctrl;
        const schedule = (delay: number) => {
            if (!stopped.current && visibleRef.current) {
                timer.current = setTimeout(read, delay);
            }
        };
        /** A wait that a tab shown again must honour too. */
        const holdOff = (delay: number) => {
            notBefore.current = Date.now() + delay;
            schedule(delay);
        };
        api.setupStatus(ctrl.signal).then(
            (s) => {
                if (ctrl.signal.aborted) {
                    return;
                }
                inflight.current = null;
                failures.current = 0;
                notBefore.current = 0;
                const steps = Array.isArray(s?.steps) ? s.steps : [];
                if (!s?.pending) {
                    finish();
                    return;
                }
                const now = Date.now();
                // Progress: a step moved, or the setup item changed (`waiting_since`).
                const key = JSON.stringify([steps, s.waiting_since]);
                if (!progress.current) {
                    const since = s.waiting_since ? Date.parse(s.waiting_since) : NaN;
                    progress.current = { started: Number.isFinite(since) ? Math.min(now, since) : now, changed: now, steps: key };
                } else if (key !== progress.current.steps) {
                    progress.current = { ...progress.current, changed: now, steps: key };
                }
                // `failed` (the setup run may still retry it) or a long stall: the support line, read slowly from then on.
                const failed = !!s.failed || now - progress.current.changed >= SETUP_STALL_MS;
                show({ phase: failed ? 'failed' : 'pending', steps, slow: now - progress.current.started >= SETUP_SLOW_MS });
                schedule(failed ? SETUP_STALLED_POLL_MS : SETUP_POLL_MS);
            },
            (e) => {
                if (ctrl.signal.aborted || (e && e.name === 'AbortError')) {
                    return;
                }
                inflight.current = null;
                const status = e instanceof OnboardingApiError ? e.status : 0;
                if (status === 401) {
                    stop();
                    return;
                }
                if (status === 429) {
                    holdOff(Math.max(SETUP_POLL_MS, (e.retryAfter || 0) * 1000));
                    return;
                }
                // Never block: before the gate is up, and on any refusal, the app shows.
                if (!progress.current || (status >= 400 && status < 500)) {
                    stop('app');
                    return;
                }
                failures.current += 1;
                if (phase.current === 'pending' && Date.now() - progress.current.changed >= SETUP_STALL_MS) {
                    show({ phase: 'failed' });
                }
                holdOff(backoffDelay(failures.current, SETUP_POLL_MS, SETUP_MAX_BACKOFF_MS));
            },
        );
    }, [api, finish, show, stop]);

    // Read now (or once a 429 / back-off wait is over) and keep reading while the tab is shown; nothing while it is hidden.
    React.useEffect(() => {
        if (visible) {
            const wait = notBefore.current - Date.now();
            if (wait <= 0) {
                read();
            } else if (!stopped.current) {
                clearTimer();
                timer.current = setTimeout(read, wait);
            }
        }
        return () => {
            clearTimer();
            inflight.current?.abort();
            inflight.current = null;
        };
    }, [visible, read]);

    // The first answer is awaited at most SETUP_FIRST_READ_WAIT_MS; a late `pending` still brings the gate up.
    React.useEffect(() => {
        if (view.phase !== 'checking') {
            return undefined;
        }
        const t = setTimeout(() => {
            if (phase.current === 'checking') {
                show({ phase: 'app' });
            }
        }, SETUP_FIRST_READ_WAIT_MS);
        return () => clearTimeout(t);
    }, [view.phase, show]);

    if (view.phase === 'app') {
        return <>{children}</>;
    }
    if (view.phase === 'checking') {
        return (
            <Box data-testid="setup-gate-checking" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} aria-label="Loading" />
            </Box>
        );
    }
    return <SetupScreen view={view} companyName={tourOperator?.name || null} onSignOut={() => logout?.()} />;
}

function SetupScreen({ view, companyName, onSignOut }: { view: View; companyName: string | null; onSignOut: () => void }) {
    const failed = view.phase === 'failed';
    const opening = view.phase === 'opening';
    const lanes = stepLanes(view.steps);
    let title = SETUP_TITLE;
    if (failed) {
        title = 'Setting up your account';
    } else if (opening) {
        title = 'Opening your dashboard…';
    }
    return (
        <Box
            component="main"
            data-testid="setup-gate"
            sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 4, bgcolor: 'background.default' }}
        >
            <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 520 }}>
                <Box>
                    {companyName && <Typography variant="overline" color="text.secondary">{companyName}</Typography>}
                    <Typography variant="h5" component="h1">{title}</Typography>
                    {!failed && !opening && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            This usually takes a minute or two. This page moves on by itself when your account is ready.
                        </Typography>
                    )}
                </Box>
                {lanes.length > 0 && <ProgressPanel lanes={lanes} />}
                {failed && (
                    <Alert severity="error" data-testid="setup-failed">
                        {SETUP_FAILED_TEXT} <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
                    </Alert>
                )}
                {!failed && !opening && view.slow && (
                    <Alert severity="info" data-testid="setup-slow">
                        {SETUP_SLOW_TEXT} Keep this page open: it moves on by itself.
                    </Alert>
                )}
                {opening && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} aria-label="Opening your dashboard" />
                    </Box>
                )}
                <Box>
                    <Button size="small" color="inherit" onClick={onSignOut}>Sign out</Button>
                </Box>
            </Stack>
        </Box>
    );
}
